import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Filter,
  Gauge,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  TimerReset,
} from 'lucide-react';

import { AlunoBuscaCard } from '@/components/busca-ativa/AlunoBuscaCard';
import { AlunoBuscaDrawer } from '@/components/busca-ativa/AlunoBuscaDrawer';
import { RegistrarContatoModal } from '@/components/busca-ativa/RegistrarContatoModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useEscolaConfig } from '@/contexts/EscolaConfigContext';
import {
  STATUS_LABELS,
  buscaAtivaService,
  gestorService,
  type AlunoCasoBuscaAtiva,
  type AlunoFaltasConsecutivasData,
  type AlunoRiscoData,
  type BuscaAtivaStatus,
  type CasoBuscaAtiva,
  type NivelRisco,
  type PresencaBuscaAtiva,
  type ResumoBuscaAtiva,
} from '@/domains';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

type ColumnId =
  | 'TRIAGEM'
  | 'AGUARDANDO'
  | 'PLANO'
  | 'OBSERVACAO'
  | 'RECUPERADOS'
  | 'EVASAO';

interface StudentContact {
  nome: string;
  telefone: string | null;
  nome_responsavel: string | null;
  turma_nome: string;
}

const COLUMN_CONFIG: Array<{
  id: ColumnId;
  title: string;
  description: string;
  statuses: BuscaAtivaStatus[];
  dropStatus: BuscaAtivaStatus;
  className: string;
  headerClassName: string;
}> = [
  {
    id: 'TRIAGEM',
    title: 'Triagem e contato',
    description: 'Novos casos e contatos ainda pendentes',
    statuses: ['NOVO_ALERTA', 'CONTATO_PENDENTE'],
    dropStatus: 'CONTATO_PENDENTE',
    className: 'bg-slate-100/80',
    headerClassName: 'text-slate-800',
  },
  {
    id: 'AGUARDANDO',
    title: 'Aguardando família',
    description: 'Tentativa realizada; resposta pendente',
    statuses: ['AGUARDANDO_FAMILIA'],
    dropStatus: 'AGUARDANDO_FAMILIA',
    className: 'bg-amber-50',
    headerClassName: 'text-amber-900',
  },
  {
    id: 'PLANO',
    title: 'Plano de apoio',
    description: 'Causa, encaminhamento ou retorno combinado',
    statuses: ['CAUSA_IDENTIFICADA', 'ENCAMINHADO', 'RETORNO_COMBINADO'],
    dropStatus: 'CAUSA_IDENTIFICADA',
    className: 'bg-indigo-50',
    headerClassName: 'text-indigo-900',
  },
  {
    id: 'OBSERVACAO',
    title: 'Em observação',
    description: 'Retornou e precisa estabilizar a frequência',
    statuses: ['EM_OBSERVACAO'],
    dropStatus: 'EM_OBSERVACAO',
    className: 'bg-sky-50',
    headerClassName: 'text-sky-900',
  },
  {
    id: 'RECUPERADOS',
    title: 'Recuperados',
    description: 'Retorno comprovado e frequência estabilizada',
    statuses: ['RECUPERADO'],
    dropStatus: 'RECUPERADO',
    className: 'bg-emerald-50',
    headerClassName: 'text-emerald-900',
  },
  {
    id: 'EVASAO',
    title: 'Evasão confirmada',
    description: 'Encerramentos formais, separados dos resgates',
    statuses: ['EVASAO_CONFIRMADA'],
    dropStatus: 'EVASAO_CONFIRMADA',
    className: 'bg-rose-50',
    headerClassName: 'text-rose-900',
  },
];

const TERMINAL_STATUSES: BuscaAtivaStatus[] = ['RECUPERADO', 'EVASAO_CONFIRMADA'];

function normalizeLegacyStatus(status?: string): BuscaAtivaStatus {
  if (status === 'AGUARDANDO_RETORNO') return 'AGUARDANDO_FAMILIA';
  if (status === 'RESOLVIDO') return 'EM_OBSERVACAO';
  if (status === 'EVASAO_CONFIRMADA') return 'EVASAO_CONFIRMADA';
  return status === 'EM_ACOMPANHAMENTO' ? 'CONTATO_PENDENTE' : 'NOVO_ALERTA';
}

function getRiskProfile({
  totalFaltas,
  consecutivas,
  resumo,
  caso,
}: {
  totalFaltas: number;
  consecutivas: number;
  resumo?: ResumoBuscaAtiva;
  caso?: CasoBuscaAtiva;
}): { score: number; nivel: NivelRisco; motivos: string[]; followUpVencido: boolean } {
  let score = 0;
  const motivos: string[] = [];
  const now = new Date();
  const followUpVencido = Boolean(
    caso?.proximo_follow_up &&
      new Date(caso.proximo_follow_up) < now &&
      !TERMINAL_STATUSES.includes(caso.status),
  );

  if (consecutivas >= 5) {
    score += 40;
    motivos.push(`${consecutivas} faltas consecutivas`);
  } else if (consecutivas >= 3) {
    score += 25;
    motivos.push(`${consecutivas} faltas consecutivas`);
  } else if (consecutivas > 0) {
    score += 10;
    motivos.push(`${consecutivas} faltas consecutivas`);
  }

  if (totalFaltas >= 15) {
    score += 25;
    motivos.push(`${totalFaltas} faltas acumuladas`);
  } else if (totalFaltas >= 8) {
    score += 15;
    motivos.push(`${totalFaltas} faltas acumuladas`);
  } else if (totalFaltas >= 5) {
    score += 8;
    motivos.push(`${totalFaltas} faltas acumuladas`);
  }

  if (!resumo?.contatado) {
    score += 15;
    motivos.push('família ainda não contatada');
  } else if (resumo.totalContatos > 1 && (totalFaltas > 0 || consecutivas > 0)) {
    score += 15;
    motivos.push(`reincidência após ${resumo.totalContatos} contatos`);
  }

  if (followUpVencido) {
    score += 15;
    motivos.push('follow-up vencido');
  }

  const boundedScore = Math.min(score, 100);
  const terminalOrObservation =
    caso && ['EM_OBSERVACAO', 'RECUPERADO'].includes(caso.status);
  const nivel: NivelRisco = terminalOrObservation
    ? 'OBSERVACAO'
    : boundedScore >= 70
      ? 'CRITICO'
      : boundedScore >= 45
        ? 'ALTO'
        : 'MEDIO';

  return {
    score: boundedScore,
    nivel,
    motivos: motivos.length ? motivos : caso?.motivos_risco || ['caso em acompanhamento'],
    followUpVencido,
  };
}

function MetricCard({
  label,
  value,
  helper,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string | number;
  helper: string;
  icon: React.ElementType;
  tone: 'rose' | 'amber' | 'indigo' | 'emerald' | 'slate';
}) {
  const tones = {
    rose: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    slate: 'bg-slate-100 text-slate-700',
  };

  return (
    <Card className="border-slate-200 shadow-none">
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <p className="text-xs font-semibold text-slate-600">{label}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{value}</p>
          <p className="mt-1 text-[11px] text-slate-500">{helper}</p>
        </div>
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', tones[tone])}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CentralBuscaAtivaPage() {
  const { user } = useAuth();
  const { config } = useEscolaConfig();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alunosRisco, setAlunosRisco] = useState<AlunoRiscoData[]>([]);
  const [alunosConsecutivos, setAlunosConsecutivos] = useState<AlunoFaltasConsecutivasData[]>([]);
  const [resumos, setResumos] = useState<Map<string, ResumoBuscaAtiva>>(new Map());
  const [casos, setCasos] = useState<Map<string, CasoBuscaAtiva>>(new Map());
  const [contatos, setContatos] = useState<Map<string, StudentContact>>(new Map());
  const [presencas, setPresencas] = useState<Map<string, PresencaBuscaAtiva[]>>(new Map());

  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('todos');
  const [actionFilter, setActionFilter] = useState('todos');
  const [visibleCount, setVisibleCount] = useState<Record<ColumnId, number>>({
    TRIAGEM: 15,
    AGUARDANDO: 15,
    PLANO: 15,
    OBSERVACAO: 15,
    RECUPERADOS: 15,
    EVASAO: 15,
  });

  const [selectedAlunoId, setSelectedAlunoId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragModalOpen, setDragModalOpen] = useState(false);
  const [dragAlunoId, setDragAlunoId] = useState<string | null>(null);
  const [dragTargetStatus, setDragTargetStatus] = useState<BuscaAtivaStatus>('CONTATO_PENDENTE');

  const fetchData = useCallback(
    async (background = false) => {
      if (!user?.escola_id) {
        setError('Não foi possível identificar a escola vinculada ao usuário.');
        setLoading(false);
        return;
      }

      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const [dashboard, persistedCases] = await Promise.all([
          gestorService.getDashboardData(user.escola_id),
          buscaAtivaService.getCasos(user.escola_id),
        ]);

        const risco = dashboard.alunosRisco || [];
        const consecutivos = dashboard.alunosConsecutivos || [];
        const idsRisco = [
          ...new Set([
            ...risco.map(item => item.aluno_id),
            ...consecutivos.map(item => item.aluno_id),
          ]),
        ];
        const allIds = [...new Set([...idsRisco, ...persistedCases.keys()])];

        const [summaryMap, attendanceMap, studentsResult] = await Promise.all([
          gestorService.getBuscaAtivaResumo(user.escola_id, allIds) as Promise<
            Map<string, ResumoBuscaAtiva>
          >,
          buscaAtivaService.getPresencasRecentes(user.escola_id, allIds),
          allIds.length
            ? supabase
                .from('alunos')
                .select('id, nome, telefone_responsavel, nome_responsavel, turmas(nome)')
                .in('id', allIds)
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (studentsResult.error) throw studentsResult.error;

        const studentMap = new Map<string, StudentContact>();
        (studentsResult.data || []).forEach((student: any) => {
          const turma = Array.isArray(student.turmas) ? student.turmas[0] : student.turmas;
          studentMap.set(student.id, {
            nome: student.nome,
            telefone: student.telefone_responsavel,
            nome_responsavel: student.nome_responsavel,
            turma_nome: turma?.nome || 'Sem turma',
          });
        });

        const riscoById = new Map(risco.map(item => [item.aluno_id, item]));
        const consecutivoById = new Map(consecutivos.map(item => [item.aluno_id, item]));
        const missingCases = idsRisco
          .filter(id => !persistedCases.has(id))
          .map(id => {
            const riscoItem = riscoById.get(id);
            const consecutivoItem = consecutivoById.get(id);
            const profile = getRiskProfile({
              totalFaltas: riscoItem?.total_faltas || 0,
              consecutivas: consecutivoItem?.contagem_faltas_consecutivas || 0,
              resumo: summaryMap.get(id),
            });
            return { alunoId: id, ...profile };
          });

        await buscaAtivaService.ensureCasos(user.escola_id, missingCases);

        setAlunosRisco(risco);
        setAlunosConsecutivos(consecutivos);
        setResumos(summaryMap);
        setCasos(persistedCases);
        setContatos(studentMap);
        setPresencas(attendanceMap);
      } catch (loadError) {
        console.error('Erro ao carregar Central de Busca Ativa:', loadError);
        setError('Não foi possível carregar todos os casos. Tente atualizar a central.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.escola_id],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const alunos = useMemo<AlunoCasoBuscaAtiva[]>(() => {
    if (!user?.escola_id) return [];

    const riscoById = new Map(alunosRisco.map(item => [item.aluno_id, item]));
    const consecutivoById = new Map(alunosConsecutivos.map(item => [item.aluno_id, item]));
    const ids = [
      ...new Set([
        ...riscoById.keys(),
        ...consecutivoById.keys(),
        ...casos.keys(),
      ]),
    ];

    return ids
      .map(id => {
        const risco = riscoById.get(id);
        const consecutivo = consecutivoById.get(id);
        const contact = contatos.get(id);
        const resumo = resumos.get(id);
        const existingCase = casos.get(id);
        const totalFaltas = risco?.total_faltas || 0;
        const consecutivas = consecutivo?.contagem_faltas_consecutivas || 0;
        const profile = getRiskProfile({
          totalFaltas,
          consecutivas,
          resumo,
          caso: existingCase,
        });

        const caso: CasoBuscaAtiva = {
          escola_id: user.escola_id!,
          aluno_id: id,
          status: existingCase?.status || normalizeLegacyStatus(resumo?.ultimoStatus),
          nivel_risco: profile.nivel,
          score_risco: profile.score,
          motivos_risco: profile.motivos,
          ...existingCase,
        };

        return {
          aluno_id: id,
          aluno_nome:
            contact?.nome || risco?.aluno_nome || consecutivo?.aluno_nome || 'Aluno sem nome',
          turma_nome:
            contact?.turma_nome || risco?.turma_nome || consecutivo?.turma_nome || 'Sem turma',
          telefone: contact?.telefone || null,
          nome_responsavel: contact?.nome_responsavel,
          isConsecutiva: consecutivas > 0,
          total_faltas: totalFaltas,
          faltas_consecutivas: consecutivas,
          score: profile.score,
          nivel: profile.nivel,
          motivos: profile.motivos,
          followUpVencido: profile.followUpVencido,
          caso: {
            ...caso,
            nivel_risco: profile.nivel,
            score_risco: profile.score,
            motivos_risco: profile.motivos,
          },
          resumo,
          presencas: presencas.get(id) || [],
        };
      })
      .sort((a, b) => {
        if (a.followUpVencido !== b.followUpVencido) return a.followUpVencido ? -1 : 1;
        return b.score - a.score;
      });
  }, [alunosConsecutivos, alunosRisco, casos, contatos, presencas, resumos, user?.escola_id]);

  const filteredAlunos = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pt-BR');
    return alunos.filter(aluno => {
      const matchesSearch =
        !normalizedSearch ||
        aluno.aluno_nome.toLocaleLowerCase('pt-BR').includes(normalizedSearch) ||
        aluno.turma_nome.toLocaleLowerCase('pt-BR').includes(normalizedSearch) ||
        aluno.nome_responsavel?.toLocaleLowerCase('pt-BR').includes(normalizedSearch);
      const matchesRisk = riskFilter === 'todos' || aluno.nivel === riskFilter;
      const matchesAction =
        actionFilter === 'todos' ||
        (actionFilter === 'vencidos' && aluno.followUpVencido) ||
        (actionFilter === 'sem_contato' && !aluno.resumo?.contatado) ||
        (actionFilter === 'sem_responsavel' && !aluno.caso.responsavel_nome);
      return matchesSearch && matchesRisk && matchesAction;
    });
  }, [actionFilter, alunos, riskFilter, search]);

  const columnData = useMemo(() => {
    return new Map(
      COLUMN_CONFIG.map(column => [
        column.id,
        filteredAlunos.filter(aluno => column.statuses.includes(aluno.caso.status)),
      ]),
    );
  }, [filteredAlunos]);

  const selectedAluno = alunos.find(item => item.aluno_id === selectedAlunoId) || null;
  const dragAluno = alunos.find(item => item.aluno_id === dragAlunoId) || null;

  const metrics = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);

    const active = alunos.filter(item => !TERMINAL_STATUSES.includes(item.caso.status));
    const criticalOverdue = active.filter(
      item => item.nivel === 'CRITICO' && (item.followUpVencido || !item.resumo?.contatado),
    ).length;
    const dueToday = active.filter(item => {
      if (!item.caso.proximo_follow_up) return false;
      const date = new Date(item.caso.proximo_follow_up);
      return date >= startOfToday && date < endOfToday;
    }).length;
    const casesWithTiming = active.filter(
      item => item.caso.created_at && item.caso.primeiro_contato_em,
    );
    const within24h = casesWithTiming.filter(item => {
      const opened = new Date(item.caso.created_at!).getTime();
      const first = new Date(item.caso.primeiro_contato_em!).getTime();
      return first - opened <= 24 * 60 * 60 * 1000;
    }).length;
    const sla =
      casesWithTiming.length > 0
        ? `${Math.round((within24h / casesWithTiming.length) * 100)}%`
        : '—';
    const recoveredMonth = alunos.filter(item => {
      if (item.caso.status !== 'RECUPERADO' || !item.caso.recuperado_em) return false;
      const date = new Date(item.caso.recuperado_em);
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length;
    const relapses = alunos.filter(
      item =>
        item.caso.status === 'RECUPERADO' &&
        (item.total_faltas > 0 || item.faltas_consecutivas > 0),
    ).length;

    return { criticalOverdue, dueToday, sla, recoveredMonth, relapses };
  }, [alunos]);

  const handleContactRegistered = useCallback(
    (
      novoRegistro?: Record<string, unknown>,
      casoAtualizado?: CasoBuscaAtiva,
      alunoId?: string,
    ) => {
      const targetId = alunoId || casoAtualizado?.aluno_id;
      if (!targetId) {
        fetchData(true);
        return;
      }

      if (casoAtualizado) {
        setCasos(previous => {
          const next = new Map(previous);
          next.set(targetId, casoAtualizado);
          return next;
        });
      }

      if (novoRegistro) {
        setResumos(previous => {
          const next = new Map(previous);
          const existing = next.get(targetId);
          const historyItem = novoRegistro as any;
          const contatoConfirmado = novoRegistro.resultado_contato !== 'WHATSAPP_ABERTO';
          next.set(targetId, {
            contatado: Boolean(existing?.contatado || contatoConfirmado),
            ultimoContato: String(novoRegistro.data_contato || new Date().toISOString()),
            totalContatos: (existing?.totalContatos || 0) + 1,
            ultimoStatus: String(novoRegistro.status_funil || casoAtualizado?.status || ''),
            historico: [historyItem, ...(existing?.historico || [])],
          });
          return next;
        });
      }
    },
    [fetchData],
  );

  const handleWhatsAppOpened = async (
    aluno: AlunoCasoBuscaAtiva,
    templateId: string,
    message: string,
  ) => {
    if (!user?.escola_id) return;

    const registro = {
      aluno_id: aluno.aluno_id,
      escola_id: user.escola_id,
      forma_contato: 'WhatsApp',
      justificativa_faltas: `WhatsApp aberto com o template "${templateId}". Confirmação de envio pendente.`,
      status_funil: aluno.caso.status,
      data_contato: new Date().toISOString(),
      created_at: new Date().toISOString(),
      monitor_responsavel: user.username || user.email || 'Equipe escolar',
      template_id: templateId,
      resultado_contato: 'WHATSAPP_ABERTO',
    };

    try {
      const { error: insertError } = await (
        supabase.from('registros_contato_busca_ativa') as any
      ).insert([registro]);
      if (insertError) throw insertError;
      handleContactRegistered(registro, undefined, aluno.aluno_id);
      toast({
        title: 'Tentativa registrada',
        description: 'O WhatsApp foi aberto. Registre a resposta quando retornar ao sistema.',
      });
    } catch (interactionError) {
      console.error(interactionError);
      toast({
        title: 'WhatsApp aberto, mas a tentativa não foi registrada',
        description: 'Verifique se a migration do CRM de Busca Ativa foi aplicada.',
        variant: 'destructive',
      });
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    const targetColumn = COLUMN_CONFIG.find(column => column.id === destination.droppableId);
    if (!targetColumn) return;

    setDragAlunoId(draggableId);
    setDragTargetStatus(targetColumn.dropStatus);
    setDragModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100dvh-64px)] bg-slate-50 p-4 md:p-6">
        <div className="mx-auto max-w-[1700px] space-y-5">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[1, 2, 3, 4, 5].map(item => (
              <Skeleton key={item} className="h-28 rounded-xl" />
            ))}
          </div>
          <div className="flex gap-5 overflow-hidden">
            {[1, 2, 3, 4].map(item => (
              <Skeleton key={item} className="h-[520px] min-w-[330px] rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-0">
      <div className="app-page max-w-[1700px]">
        <header className="app-page-header">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-600">
              Permanência escolar
            </p>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-950 md:text-3xl">
              <ShieldAlert className="h-7 w-7 text-indigo-600" />
              Central de recuperação
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Priorize casos, compreenda barreiras e acompanhe o aluno até a frequência estabilizar.
            </p>
          </div>
          <Button
            variant="outline"
            className="min-h-10 bg-white"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            Atualizar casos
          </Button>
        </header>

        {error && (
          <div className="flex flex-col justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 sm:flex-row sm:items-center" role="alert">
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0" /> {error}
            </span>
            <Button variant="outline" size="sm" className="bg-white" onClick={() => fetchData(true)}>
              Tentar novamente
            </Button>
          </div>
        )}

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Indicadores da Busca Ativa">
          <MetricCard
            label="Críticos vencidos"
            value={metrics.criticalOverdue}
            helper="exigem intervenção hoje"
            icon={AlertTriangle}
            tone="rose"
          />
          <MetricCard
            label="Follow-ups hoje"
            value={metrics.dueToday}
            helper="próximas ações agendadas"
            icon={CalendarClock}
            tone="amber"
          />
          <MetricCard
            label="Contato em 24h"
            value={metrics.sla}
            helper="dos casos com tempo registrado"
            icon={Clock3}
            tone="indigo"
          />
          <MetricCard
            label="Recuperados no mês"
            value={metrics.recoveredMonth}
            helper="retorno e estabilidade confirmados"
            icon={CheckCircle2}
            tone="emerald"
          />
          <MetricCard
            label="Reincidências"
            value={metrics.relapses}
            helper="recuperados que voltaram ao risco"
            icon={RotateCcw}
            tone="slate"
          />
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Buscar aluno, turma ou responsável..."
                className="min-h-10 pl-9"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:w-[520px]">
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger className="min-h-10 bg-white">
                  <Gauge className="mr-2 h-4 w-4 text-slate-500" />
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas as prioridades</SelectItem>
                  <SelectItem value="CRITICO">Crítico</SelectItem>
                  <SelectItem value="ALTO">Alto</SelectItem>
                  <SelectItem value="MEDIO">Médio</SelectItem>
                  <SelectItem value="OBSERVACAO">Em observação</SelectItem>
                </SelectContent>
              </Select>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="min-h-10 bg-white">
                  <Filter className="mr-2 h-4 w-4 text-slate-500" />
                  <SelectValue placeholder="Situação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas as situações</SelectItem>
                  <SelectItem value="vencidos">Follow-up vencido</SelectItem>
                  <SelectItem value="sem_contato">Sem contato</SelectItem>
                  <SelectItem value="sem_responsavel">Sem responsável interno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>{filteredAlunos.length} caso(s) no escopo atual</span>
            <span className="flex items-center gap-1.5">
              <TimerReset className="h-3.5 w-3.5" />
              Casos são ordenados por prazo vencido e score de risco.
            </span>
          </div>
        </section>

        <section className="-mx-4 overflow-x-auto px-4 pb-3 md:-mx-6 md:px-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex min-h-[560px] w-max gap-4">
              {COLUMN_CONFIG.map(column => {
                const items = columnData.get(column.id) || [];
                const shown = items.slice(0, visibleCount[column.id]);
                return (
                  <Droppable key={column.id} droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={cn(
                          'flex h-[calc(100dvh-330px)] min-h-[560px] w-[330px] flex-col overflow-hidden rounded-2xl border border-slate-200 transition',
                          column.className,
                          snapshot.isDraggingOver && 'ring-2 ring-indigo-300',
                        )}
                      >
                        <div className="border-b border-white/80 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <h2 className={cn('text-sm font-bold', column.headerClassName)}>
                              {column.title}
                            </h2>
                            <Badge variant="outline" className="bg-white text-slate-700">
                              {items.length}
                            </Badge>
                          </div>
                          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                            {column.description}
                          </p>
                        </div>

                        <div className="flex-1 space-y-3 overflow-y-auto p-3">
                          {shown.map((aluno, index) => (
                            <Draggable key={aluno.aluno_id} draggableId={aluno.aluno_id} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={{
                                    ...dragProvided.draggableProps.style,
                                    filter: dragSnapshot.isDragging
                                      ? 'drop-shadow(0 12px 18px rgba(15,23,42,.18))'
                                      : undefined,
                                  }}
                                >
                                  <AlunoBuscaCard
                                    aluno={aluno}
                                    escolaId={user?.escola_id || ''}
                                    escolaNome={config?.nome}
                                    onContactRegistered={(registro, caso) =>
                                      handleContactRegistered(registro, caso, aluno.aluno_id)
                                    }
                                    onOpenDetails={() => {
                                      setSelectedAlunoId(aluno.aluno_id);
                                      setDrawerOpen(true);
                                    }}
                                    onWhatsAppOpened={(templateId, message) =>
                                      handleWhatsAppOpened(aluno, templateId, message)
                                    }
                                    innerRef={undefined}
                                    draggableProps={undefined}
                                    dragHandleProps={dragProvided.dragHandleProps}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}

                          {items.length === 0 && (
                            <div className="flex min-h-36 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white/60 p-5 text-center">
                              <CheckCircle2 className="h-7 w-7 text-slate-300" />
                              <p className="mt-2 text-xs font-medium text-slate-500">
                                Nenhum caso nesta etapa
                              </p>
                            </div>
                          )}

                          {items.length > visibleCount[column.id] && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full"
                              onClick={() =>
                                setVisibleCount(previous => ({
                                  ...previous,
                                  [column.id]: previous[column.id] + 15,
                                }))
                              }
                            >
                              Ver mais ({items.length - visibleCount[column.id]})
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        </section>
      </div>

      <AlunoBuscaDrawer
        aluno={selectedAluno}
        escolaId={user?.escola_id || ''}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onContactRegistered={(registro, caso) =>
          handleContactRegistered(registro, caso, selectedAluno?.aluno_id)
        }
      />

      {dragAluno && (
        <RegistrarContatoModal
          alunoId={dragAluno.aluno_id}
          nomeAluno={dragAluno.aluno_nome}
          escolaId={user?.escola_id || ''}
          caso={dragAluno.caso}
          open={dragModalOpen}
          onOpenChange={open => {
            setDragModalOpen(open);
            if (!open) setDragAlunoId(null);
          }}
          hideTrigger
          defaultStatusFunil={dragTargetStatus}
          onSuccess={(registro, caso) => {
            handleContactRegistered(registro, caso, dragAluno.aluno_id);
            setDragModalOpen(false);
            setDragAlunoId(null);
          }}
        />
      )}
    </main>
  );
}
